// SPDX-FileCopyrightText: 2019-2022 Matus Faro <matus@smotana.com>
// SPDX-License-Identifier: Apache-2.0
import { Button, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import * as Client from '../../api/client';
import { ReduxState, Server } from '../../api/server';
import { Orientation, contentScrollApplyStyles } from '../../common/ContentScroll';
import ErrorMsg from '../ErrorMsg';
import TimeAgoI18n from '../utils/TimeAgoI18n';

const styles = (theme: Theme) => createStyles({
  table: (props: Props) => ({
    whiteSpace: 'nowrap',
    ...contentScrollApplyStyles({
      theme,
      orientation: Orientation.Horizontal,
      backgroundColor: props.isInsidePaper ? theme.palette.background.paper : undefined,
    }),
  }),
  noNotificationsLabel: {
    margin: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  button: {
    margin: 'auto',
    display: 'block',
  },
});

interface Props {
  className?: string;
  server: Server;
  isInsidePaper?: boolean;
}

interface ConnectProps {
  callOnMount?: () => void,
  userId?: string;
  notifications?: Client.Notification[];
  getNextNotifications?: () => void;
}

class NotificationList extends Component<Props & ConnectProps & WithTranslation<'app'> & WithStyles<typeof styles, true> & RouteComponentProps> {

  constructor(props) {
    super(props);

    props.callOnMount?.();
  }

  render() {
    if (!this.props.userId) {
      return (<ErrorMsg msg='You need to log in to see your notifications' variant='info' />);
    }
    const hasNotifications = this.props.notifications && this.props.notifications.length > 0;
    return (
      <div className={this.props.className}>
        <div className={this.props.classes.table}>
          <Table size='medium'>
            <TableBody>
              {!hasNotifications ? (
                <Typography
                  className={this.props.classes.noNotificationsLabel}
                  variant='overline'
                >{this.props.t('no-notifications')}</Typography>
              ) : this.props.notifications!.map(notification => (
                <TableRow
                  key={notification.notificationId}
                  hover
                  component={Link}
                  {...{
                    style: { textDecoration: 'none' },
                    to: this.getNotificationTo(notification),
                    onClick: () => this.clearNotification(notification),
                  }}
                >
                  <TableCell key='date'><Typography><TimeAgoI18n date={notification.created} /></Typography></TableCell>
                  <TableCell key='description'><Typography>{notification.description}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {hasNotifications && (
          <Button fullWidth className={this.props.classes.button} onClick={() => this.clearAll()}>
            {this.props.t('clear-all')}
          </Button>
        )}
        {this.props.getNextNotifications && (
          <Button fullWidth className={this.props.classes.button} onClick={() => this.props.getNextNotifications && this.props.getNextNotifications()}>
            {this.props.t('show-more')}
          </Button>
        )}
      </div>
    );
  }

  getNotificationTo(notification: Client.Notification): string {
    if (notification.relatedIdeaId) {
      if (notification.relatedCommentId) {
        return `/post/${notification.relatedIdeaId}/comment/${notification.relatedCommentId}`;
      } else {
        return `/post/${notification.relatedIdeaId}`;
      }
    } else {
      return `/account`;
    }
  }

  clearNotification(notification: Client.Notification) {
    this.props.server.dispatch().then(d => d.notificationClear({
      projectId: this.props.server.getProjectId(),
      notificationId: notification.notificationId,
    }));
  }

  clearAll() {
    this.props.server.dispatch().then(d => d.notificationClearAll({
      projectId: this.props.server.getProjectId(),
    }));
  }
}

export default connect<ConnectProps, {}, Props, ReduxState>((state, ownProps) => {
  const userId = state.users.loggedIn.user ? state.users.loggedIn.user.userId : undefined;
  var getNextNotifications;
  var callOnMount;
  if (userId && state.notifications.notificationSearch.status === undefined) {
    callOnMount = () => {
      ownProps.server.dispatch().then(d => d.notificationSearch({
        projectId: ownProps.server.getProjectId(),
      }));
    };
  } else if (userId && state.notifications.notificationSearch.cursor) {
    getNextNotifications = () => ownProps.server.dispatch().then(d => d.notificationSearch({
      projectId: ownProps.server.getProjectId(),
      cursor: state.notifications.notificationSearch.cursor,
    }));
  }
  const connectProps: ConnectProps = {
    callOnMount,
    userId: userId,
    notifications: state.notifications.notificationSearch.notifications,
    getNextNotifications: getNextNotifications,
  };
  return connectProps;
}, null, null, { forwardRef: true })(withStyles(styles, { withTheme: true })(withRouter(withTranslation('app', { withRef: true })(NotificationList))));
